/**
 * FlowVariablesService
 *
 * Firebase CRUD for FlowDocument.variables stored on the version document.
 * Variables live at flow-document level: projects/{projectId}/onboardings/{onboardingId}/versions/{versionId}
 * as the `variables` field on the version document.
 *
 * This service reads/writes individual FunnelVariable entries within that field
 * using Firestore merge writes to avoid overwriting other version document fields.
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import type { FunnelVariable } from '@segmently/schema-models/v2';

/**
 * Recursively strip `undefined` values from an object.
 * Firestore rejects documents containing `undefined` at any depth.
 */
function stripUndefined(obj: unknown): unknown {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (value !== undefined) {
        result[key] = stripUndefined(value);
      }
    }
    return result;
  }
  return obj;
}

export class FlowVariablesService {
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  private getVersionDocRef(projectId: string, onboardingId: string, versionId: string) {
    return doc(
      this.firestore,
      `projects/${projectId}/onboardings/${onboardingId}/versions/${versionId}`,
    );
  }

  /**
   * Get all variables from the version document.
   */
  async getVariables(
    projectId: string,
    onboardingId: string,
    versionId: string,
  ): Promise<Record<string, FunnelVariable>> {
    try {
      const docRef = this.getVersionDocRef(projectId, onboardingId, versionId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return {};
      }

      const data = docSnap.data();
      return (data?.variables as Record<string, FunnelVariable>) ?? {};
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'FlowVariablesService.getVariables');
      throw error;
    }
  }

  /**
   * Save a single variable (create or update).
   * Uses updateDoc with dot-notation field path to REPLACE the entire variable
   * object without deep-merging nested fields (e.g. screenBindings keys).
   *
   * NOTE: setDoc + merge:true caused stale nested keys to persist because
   * Firestore deep-merges at every nesting level. updateDoc with a flat
   * field path replaces the target field atomically.
   */
  async saveVariable(
    projectId: string,
    onboardingId: string,
    versionId: string,
    variable: FunnelVariable,
  ): Promise<void> {
    try {
      const docRef = this.getVersionDocRef(projectId, onboardingId, versionId);
      const sanitized = stripUndefined(variable) as FunnelVariable;

      try {
        // Prefer updateDoc — replaces the variable atomically without deep-merging nested keys
        await updateDoc(docRef, {
          [`variables.${variable.id}`]: sanitized,
        });
      } catch (updateError: unknown) {
        // updateDoc fails if the document doesn't exist yet (first variable on a new version).
        // Fall back to setDoc with merge for document creation.
        const isNotFound = updateError instanceof Error && updateError.message?.includes('No document to update');
        if (isNotFound) {
          await setDoc(
            docRef,
            { variables: { [variable.id]: sanitized } },
            { merge: true },
          );
        } else {
          throw updateError;
        }
      }
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'FlowVariablesService.saveVariable');
      throw error;
    }
  }

  /**
   * Delete a single variable by ID.
   * Uses Firestore deleteField() sentinel for atomic removal.
   */
  async deleteVariable(
    projectId: string,
    onboardingId: string,
    versionId: string,
    variableId: string,
  ): Promise<void> {
    try {
      const docRef = this.getVersionDocRef(projectId, onboardingId, versionId);

      await setDoc(
        docRef,
        { variables: { [variableId]: deleteField() } },
        { merge: true },
      );
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'FlowVariablesService.deleteVariable');
      throw error;
    }
  }

  /**
   * Replace all variables at once.
   * Uses updateDoc with dot-notation to fully replace the `variables` field.
   */
  async saveAllVariables(
    projectId: string,
    onboardingId: string,
    versionId: string,
    variables: Record<string, FunnelVariable>,
  ): Promise<void> {
    try {
      const docRef = this.getVersionDocRef(projectId, onboardingId, versionId);
      const sanitized = stripUndefined(variables) as Record<string, FunnelVariable>;

      try {
        await updateDoc(docRef, { variables: sanitized });
      } catch (updateError: unknown) {
        const isNotFound = updateError instanceof Error && updateError.message?.includes('No document to update');
        if (isNotFound) {
          await setDoc(docRef, { variables: sanitized }, { merge: true });
        } else {
          throw updateError;
        }
      }
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'FlowVariablesService.saveAllVariables');
      throw error;
    }
  }
}

/** Singleton instance */
export const flowVariablesService = new FlowVariablesService();
